import React from 'react';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import _ from 'lodash';
import { dollarFormat } from 'utils/utils';
import { handleDateTimeOffset, convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import constants from 'constants/index';
import GlobalTooltip from 'components/GlobalTooltip';

/**
 * ID column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @param {Function} params.onView - View handler function
 * @returns {JSX.Element} - ID cell
 */
export const idBody = (params) => {
  const { rowData, onView } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">ID</span>
        <span>-</span>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <span
        onClick={() => {
          onView?.(rowData);
        }}
        className="w-100"
      >
        <span className="p-column-title text-break">ID</span>
        <span className={'text-break text-primary-dark text-decoration-none'} role={'button'} title={rowData.payment_request_number}>
          {rowData.payment_request_number}
        </span>
      </span>
    </React.Fragment>
  );
};

/**
 * Case column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Case cell
 */
export const caseBody = (params) => {
  const { rowData } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Case</span>
        <span>-</span>
      </React.Fragment>
    );
  }

  const { case_id, is_lead } = rowData;
  const mainModuleName = is_lead ? '/leads' : '/cases';
  let route = case_id ? `${mainModuleName}/${case_id}` : mainModuleName;

  return (
    <React.Fragment>
      <span className="p-column-title text-break">Case</span>
      <Link to={route} className="redirection-link-no-text-transform" title={rowData.case_name ? rowData.case_name : undefined}>
        {rowData.case_name ? rowData.case_name : '-'}
      </Link>
    </React.Fragment>
  );
};

/**
 * Viewed column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @param {Object} params.userContext - User context
 * @returns {JSX.Element} - Viewed cell
 */
export const viewBody = (params) => {
  const { rowData, userContext } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Viewed</span>
        <span>-</span>
      </React.Fragment>
    );
  }

  let viewed_status = rowData.viewed_status ? rowData.viewed_status : 'Not Viewed';

  if (viewed_status !== 'Not Viewed') {
    viewed_status = handleDateTimeOffset(userContext?.userDetails?.timezone, viewed_status, constants.month_date_year_time_12_format);
  }

  return (
    <React.Fragment>
      <span className="p-column-title text-break">Viewed</span>
      <span className="text-break" title={viewed_status}>
        {viewed_status}
      </span>
    </React.Fragment>
  );
};

/**
 * Status column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Status cell
 */
export const statusBody = (params) => {
  const { rowData } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Status</span>
        <span>-</span>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <span className="p-column-title text-break">Status</span>
      <span
        className="text-break"
        title={rowData.payment_request_status ? _.startCase(_.toLower(rowData.payment_request_status)) : undefined}
      >
        {rowData.payment_request_status ? _.startCase(_.toLower(rowData.payment_request_status)) : ''}
      </span>
    </React.Fragment>
  );
};

/**
 * Total column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Total cell
 */
export const totalBody = (params) => {
  const { rowData } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Total</span>
        <span>-</span>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <span className="p-column-title text-break">Total</span>
      <span className="text-break" title={dollarFormat(rowData.amount)}>
        {dollarFormat(rowData.amount)}
      </span>
    </React.Fragment>
  );
};

/**
 * Due column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Due cell
 */
export const dueBody = (params) => {
  const { rowData } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Due</span>
        <span>-</span>
      </React.Fragment>
    );
  }

  const dueAmount = rowData?.due_amount ? dollarFormat(rowData?.due_amount) : dollarFormat(0.0);

  return (
    <React.Fragment>
      <span className="p-column-title text-break">Due</span>
      <span className="text-break" title={dueAmount}>
        {dueAmount}
      </span>
    </React.Fragment>
  );
};

/**
 * Due Today column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Due Today cell
 */
export const dueTodayBody = (params) => {
  const { rowData } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Due Today</span>
        <span>-</span>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <span className="p-column-title text-break">Due Today</span>
      <span className="text-break" title={rowData?.due_today ? dollarFormat(rowData?.due_today) : dollarFormat(0.0)}>
        {rowData?.due_today ? dollarFormat(rowData?.due_today) : dollarFormat(0.0)}
      </span>
    </React.Fragment>
  );
};

/**
 * Next Due Date column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @param {Object} params.userContext - User context
 * @returns {JSX.Element} - Next Due Date cell
 */
export const nextDueDateBody = (params) => {
  const { rowData, userContext } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Next Due Date</span>
        <span>-</span>
      </React.Fragment>
    );
  }

  const formattedDate = convertDateFromApiToUserTimezone(
    rowData?.due_date,
    userContext?.userDetails?.timezone,
    constants.month_date_year_format
  );

  return (
    <React.Fragment>
      <span className="p-column-title text-break">Next Due Date</span>
      <span
        className="text-break"
        title={
          rowData?.amount?.toString() === rowData?.paid_amount?.toString() || rowData?.payment_request_status === 'PAID'
            ? undefined
            : formattedDate !== '-'
            ? formattedDate
            : undefined
        }
      >
        {rowData?.amount?.toString() === rowData?.paid_amount?.toString() || rowData?.payment_request_status === 'PAID'
          ? '-'
          : formattedDate}
      </span>
    </React.Fragment>
  );
};

/**
 * Sent column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @param {Object} params.userContext - User context
 * @returns {JSX.Element} - Sent cell
 */
export const sentBody = (params) => {
  const { rowData, userContext } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Sent</span>
        <span>-</span>
      </React.Fragment>
    );
  }

  const formattedDate = convertDateFromApiToUserTimezone(
    rowData.payment_request_date,
    userContext?.userDetails?.timezone,
    constants.month_date_year_format
  );

  return (
    <React.Fragment>
      <span className="p-column-title text-break">Sent</span>
      <span className="text-break" title={formattedDate !== '-' ? formattedDate : undefined}>
        {formattedDate}
      </span>
    </React.Fragment>
  );
};

/**
 * Action column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @param {Function} params.onSendMail - Send mail handler function
 * @param {Function} params.onRecordPayment - Record payment handler function
 * @param {Function} params.onAcceptPayment - Accept payment handler function
 * @param {Function} params.onConfidoPay - Confido pay handler function
 * @param {boolean} params.isConfidoLinkLoading - Whether Confido payment link is loading
 * @param {Function} params.onReminder - Reminder handler function
 * @param {Function} params.onDelete - Delete handler function
 * @param {Object} params.userContext - User context
 * @returns {JSX.Element} - Action cell
 */
export const actionBody = (params) => {
  const { rowData, onSendMail, onRecordPayment, onAcceptPayment, onConfidoPay, isConfidoLinkLoading, onReminder, onDelete, userContext } =
    params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Action</span>
        <span>-</span>
      </React.Fragment>
    );
  }

  let due_amount = rowData?.due_amount;
  due_amount = due_amount ? parseInt(due_amount) : 0;

  return (
    <React.Fragment>
      <div className="text-nowrap w-100">
        <span className="p-column-title text-break">Action</span>
        {due_amount > 0 && (
          <span role="button" onClick={(e) => onSendMail?.(rowData)}>
            <GlobalTooltip tooltip="Payment Request Reminder">
              <i className="icon-send-form"></i>
            </GlobalTooltip>
          </span>
        )}

        {due_amount > 0 && (
          <span role="button">
            <GlobalTooltip tooltip="Record Payment" className={'ms-2'}>
              <i className="icon-radio" onClick={(e) => onRecordPayment?.(rowData)} />
            </GlobalTooltip>
          </span>
        )}

        {due_amount > 0 && userContext?.userDetails?.is_quickbook_connected && (
          <span role="button">
            <GlobalTooltip tooltip="Pay" className={'ms-2'}>
              <i className="bi bi-credit-card-2-back" style={{ color: '#7f8f9f' }} onClick={() => onAcceptPayment?.(rowData)} />
            </GlobalTooltip>
          </span>
        )}

        {due_amount > 0 && userContext?.userDetails?.is_confido_connected && (
          <span role="button">
            <GlobalTooltip
              tooltip={
                rowData?.confido_bank_account_id ? 'Pay' : 'No Confido Bank Account mapped with this payment request deposit account'
              }
              className={'ms-2'}
            >
              <i
                className={`bi bi-credit-card-2-back${isConfidoLinkLoading || !rowData?.confido_bank_account_id ? ' p-disabled' : ''}`}
                style={{ color: '#7f8f9f' }}
                onClick={() => onConfidoPay?.(rowData)}
              />
            </GlobalTooltip>
          </span>
        )}

        {due_amount > 0 && (
          <span role="button" onClick={(e) => onReminder?.(rowData)}>
            <GlobalTooltip tooltip="Reminder" className={'ms-2'}>
              <i className="icon-notification" />
            </GlobalTooltip>
          </span>
        )}

        {rowData && !rowData.is_payment_record && (
          <span role="button">
            <GlobalTooltip tooltip="Delete" className={'ms-2'}>
              <i className="icon-delete" onClick={(e) => onDelete?.(rowData)} />
            </GlobalTooltip>
          </span>
        )}
      </div>
    </React.Fragment>
  );
};
